import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import { ListComponent, VerticalSpacing } from '@custom-components';
import { NUM_PER_PAGE } from './helperFunctions';

function List(props) {
  const {
    data,
    loading,
    error,
    viewFilters,
    sortFilters,
    initialViewFilterId,
    initialSortFilterId,
    initialQueryValue,
    initialToggleValue,
    tableComponent,
    cardComponent,
    helpText,
    noDataObj,
    searchBy,
    listComponentProps,
    otherComponentProps,
    headerComponentProps,
    header,
    pagination,
    organizationId,
    mutations,
    screenClass
  } = props;

  const [currentPage, setCurrentPage] = useState(get(props, 'initialPaginationValue.currentPage', 1));
  const [perPageIndex, setPerPageIndex] = useState(get(props, 'initialPaginationValue.perPageIndex', 0));

  /**
   * When pagination params are changed, call the onPaginationChange method
   */
  const handlePagination = (params) => {
    const { interactionHooks: { onPaginationChange = () => {} } = {} } = listComponentProps ?? {};

    onPaginationChange(params);
  };

  /**
   * Jump to the first page
   */
  const firstPage = () => {
    const update = { currentPage: 1 };

    setCurrentPage(1);
    handlePagination(update);
  };

  useEffect(() => {
    firstPage();
  }, [initialQueryValue]);

  const getPerPage = (index) => {
    if (index < 0) return undefined;
    return NUM_PER_PAGE[screenClass][index];
  };

  /**
   * Pagination Controls.
   *
   * Sets state: currentPage.
   */
  const nextPage = (total) => {
    const totalPages = Math.ceil(total / getPerPage(perPageIndex));

    const nextPageIndex = currentPage + 1 <= totalPages ? currentPage + 1 : currentPage;

    const update = {
      currentPage: nextPageIndex
    };
    setCurrentPage(nextPageIndex);
    handlePagination(update);
  };

  /**
   * Pagination Controls.
   *
   * Sets state: currentPage.
   */
  const prevPage = () => {
    const prevPageIndex = currentPage - 1 >= 1 ? currentPage - 1 : currentPage;
    const update = {
      currentPage: prevPageIndex
    };
    setCurrentPage(prevPageIndex);
    handlePagination(update);
  };

  /**
   * Sets items per page
   *
   * Sets state: perPage.
   */
  const handleSelect = (e) => {
    const newPerPageIndex = NUM_PER_PAGE[screenClass].indexOf(parseInt(e.target.value, 10));

    const change = {
      newPerPageIndex,
      currentPage: 1
    };

    setPerPageIndex(newPerPageIndex);
    setCurrentPage(1);
    handlePagination(change);
  };

  return (
    <ListComponent
      data={data}
      searchBy={searchBy}
      useFilters={{
        sort: sortFilters,
        view: viewFilters
      }}
      initialViewFilterId={initialViewFilterId || viewFilters[0].id}
      initialSortFilterId={initialSortFilterId}
      initialQueryValue={initialQueryValue}
      defaultView={initialToggleValue}
      page={currentPage}
      perPage={getPerPage(perPageIndex)}
      {...listComponentProps}
    >
      {({
        currentView,
        screenClass: newScreenClass,
        constants,
        filteredData,
        headerProps,
        paginationMessages,
        paginationProps
      }) => {
        // The below line seems deprecated.
        const Component = currentView === constants.CARD_VIEW ? cardComponent : tableComponent;
        const newProps = { data: filteredData, ...otherComponentProps, organizationId, mutations };
        const noData = filteredData.length < 1;
        const headerInfo = { ...headerProps, helpText, ...headerComponentProps };
        if (loading)
          return (
            <>
              {header && <ListComponent.Header {...headerInfo} />}
              <Component {...newProps} screenClass={newScreenClass} />
              <ListComponent.Loading />
            </>
          );
        if (error)
          return (
            <>
              {header && <ListComponent.Header {...headerInfo} />}
              <Component {...newProps} screenClass={newScreenClass} />

              <VerticalSpacing>
                <p>{`Error! ${error.message}`}</p>
              </VerticalSpacing>
            </>
          );
        return (
          <>
            {header && <ListComponent.Header {...headerInfo} />}
            {noData ? (
              <ListComponent.NoData
                actionLabel={null}
                icon="facilities"
                message={noDataObj.message}
                title={noDataObj.title}
              />
            ) : (
              <Component {...newProps} screenClass={newScreenClass} />
            )}
            {pagination && (
              <ListComponent.Pagination
                {...paginationProps}
                prevPage={prevPage}
                nextPage={() => nextPage(paginationProps.itemTotal)}
                selectOptions={NUM_PER_PAGE[newScreenClass]}
                selectFunc={handleSelect}
                messages={paginationMessages}
                showAll={false}
              />
            )}
          </>
        );
      }}
    </ListComponent>
  );
}

List.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string
  }),
  viewFilters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  ),
  sortFilters: PropTypes.arrayOf(PropTypes.shape({})),
  initialViewFilterId: PropTypes.string,
  initialSortFilterId: PropTypes.string,
  initialQueryValue: PropTypes.string,
  initialToggleValue: PropTypes.string,
  initialPaginationValue: PropTypes.shape({
    currentPage: PropTypes.number,
    perPageIndex: PropTypes.number
  }),
  screenClass: PropTypes.string.isRequired,
  tableComponent: PropTypes.func.isRequired,
  cardComponent: PropTypes.func,
  helpText: PropTypes.string,
  noDataObj: PropTypes.shape({ message: PropTypes.string, title: PropTypes.string }),
  searchBy: PropTypes.arrayOf(PropTypes.string),
  listComponentProps: PropTypes.shape({
    interactionHooks: PropTypes.shape({
      onPaginationChange: PropTypes.func.isRequired
    }).isRequired
  }),
  otherComponentProps: PropTypes.shape({}),
  headerComponentProps: PropTypes.shape({}),
  header: PropTypes.bool,
  pagination: PropTypes.bool,
  organizationId: PropTypes.string.isRequired,
  mutations: PropTypes.shape({}).isRequired
};

List.defaultProps = {
  loading: true,
  error: null,
  viewFilters: [],
  sortFilters: [],
  initialSortFilterId: undefined,
  initialViewFilterId: undefined,
  initialQueryValue: undefined,
  initialToggleValue: undefined,
  initialPaginationValue: {},
  helpText: '',
  noDataObj: { message: '', title: 'No Data' },
  searchBy: [],
  listComponentProps: {},
  otherComponentProps: {},
  headerComponentProps: {},
  header: true,
  pagination: true,
  cardComponent: () => null
};

export default List;
